import { Fragment, useRef, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'src/utils/i18n'
import dayjs from 'dayjs'
import Image from 'next/image'
import { useAndroidDevicePagedData } from 'src/hooks/useAndroidDevicePagedData'
import { buildTimeParams, buildPresetTimeParams } from 'src/utils/dateRangeParams'
import { motion } from 'framer-motion'
import { DatePicker } from 'antd'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import ContentLoading from 'src/components/ContentLoading'
import DateRangeFilter from 'src/components/DateRangeFilter'

const { RangePicker } = DatePicker

const AndroidBrowserHistory = ({ }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('common')
  const [allData, setAllData] = useState([])//所有的数据
  const [showData, setShowData] = useState([])//展示的数据
  const [showDate, setShowDate] = useState(false)//展示日期
  const [date, setDate] = useState(['All'])//日期
  const [from, setFrom] = useState('')//开始日期
  const [to, setTo] = useState('')//结束日期
  const [fromTime, setFromTime] = useState('')//开始日期时间戳
  const [toTime, setToTime] = useState('')//结束日期时间戳
  const rangeDom = useRef(null)
  const [dates, setDates] = useState([])
  const { context, data: deviceData, loading: isLoading, loadNext, setDateRange } = useAndroidDevicePagedData({
    source: 'local',
    category: 'CHROME_HISTORY',
    limit: 100
  })

  useEffect(() => {
    setAllData(deviceData)
  }, [deviceData])

  useEffect(() => {
    setShowData([])
    setShowDate(false)
    setDate(['All'])
    setFrom('')
    setTo('')
    setFromTime('')
    setToTime('')
    setDates([])
  }, [context?.requestKey])

  const dom = useRef(null)

  // 滚动百分比
  const [scroll, setScroll] = useState(0)

  // 获取滚动百分比
  useEffect(() => {
    const handScroll = () => {
      if (dom.current) {
        const { scrollTop, scrollHeight, clientHeight } = dom.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }
    const currentRef = dom.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handScroll)
      }
    }
  }, [])

  // 监听滚动百分比
  useEffect(() => {
    if (scroll >= 100) loadNext()
  }, [loadNext, scroll])

  //更新 fromTime 和 toTime（同时把区间交给接口筛选）
  useEffect(() => {
    if (date.length === 1 && date[0] !== 'All') {
      const { startTime, endTime } = buildPresetTimeParams(date[0])
      setFromTime(startTime)
      setToTime(endTime)
      setDateRange({ startTime, endTime })
    } else if (date.length === 2) {
      const { startTime, endTime } = buildTimeParams(date)
      setFromTime(startTime)
      setToTime(endTime)
      setDateRange({ startTime, endTime })
    } else {
      setFromTime('')
      setToTime('')
      setDateRange(null)
    }
  }, [date, setDateRange])

  // 日期按钮
  const button = () => (
    <div
      className='w-[calc(50%-5px)] smj:w-[172px] h-[40px] bg-[#2A99FF] border border-[#E5E7EB] rounded-[8px] flex justify-center items-center cursor-pointer relative px-[10px] Blds:w-auto Blds:min-w-[172px]'
      onClick={e => {
        e.stopPropagation()
        setShowDate(val => !val)
      }}
    >
      <Image className='ml-[2px]' src='/images/record-screen/calendar2.svg' width={20} height={20} alt='calendar' />
      <span className='text-[14px] leading-[16px] text-white mx-[10px]'>
        {date.length === 1 && date[0]}
        {date.length === 2 && (
          <>
            {date[0]} ~ {date[1]}
          </>
        )}
      </span>
      <motion.div initial={{ rotate: 0 }} animate={{ rotate: showDate ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <Image src='/images/record-screen/union.svg' width={16} height={16} alt='drop down' />
      </motion.div>

      {/* 下拉框 */}
      {showDate && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className='w-full drop-shadow-[0_0_3px_rgba(0,0,0,0.1)] bg-[#FFF] rounded-[6px] flex flex-col text-[14px] text-[#666666] absolute top-[38px] p-[10px] z-30 relative'
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <div className='border border-[#E5E5E5] rounded-[3px] text-[14px] text-[rgba(0,0,0,0.9)]'>
            <div
              className='py-[3px] px-[16px] hover:text-[#2A99FF]'
              onClick={() => {
                setDate(['All'])
                setShowDate(false)
              }}
            >
              {t('All')}
            </div>
            <div
              className='py-[3px] px-[16px] hover:text-[#2A99FF]'
              onClick={() => {
                setDate(['Today'])
                setShowDate(false)
              }}
            >
              {t('Today')}
            </div>
            <div
              className='py-[3px] px-[16px] hover:text-[#2A99FF]'
              onClick={() => {
                setDate(['Last 7 days'])
                setShowDate(false)
              }}
            >
              {t('Last 7 days')}
            </div>
            <div
              className='py-[3px] px-[16px] hover:text-[#2A99FF]'
              onClick={() => {
                setDate(['Last 14 days'])
                setShowDate(false)
              }}
            >
              {t('Last 14 days')}
            </div>
            <div
              className='py-[3px] px-[16px] hover:text-[#2A99FF]'
              onClick={() => {
                setDate(['Last 30 days'])
                setShowDate(false)
              }}
            >
              {t('Last 30 days')}
            </div>
            <div
              className='py-[3px] px-[16px] hover:text-[#2A99FF]'
              onClick={() => {
                const currentDate = new Date()
                const xxDaysAgo = currentDate.setDate(currentDate.getDate() - 365)
                setDate(['Last Year'])
                setShowDate(false)
              }}
            >
              {t('Last Year')}
            </div>
          </div>
          <div className='w-full relative'>
            <div className='text-[12px] text-[#666666] mt-[10px]'>{t('From')}</div>
            <div className='border border-[#E5E5E5] rounded-[3px] h-[24px] w-full text-[10px] text-[rgba(0,0,0,0.9)] px-[16px] flex items-center mb-[5px]'>
              {from}
            </div>
            {t('To')}
            <div className='border border-[#E5E5E5] rounded-[3px] h-[24px] w-full text-[10px] text-[rgba(0,0,0,0.9)] px-[16px] flex items-center'>
              {to}
            </div>
            <RangePicker
              className='absolute w-full h-full top-0 left-0 opacity-0'
              ref={rangeDom}
              value={dates}
              onOpenChange={open => {
              }}
              onCalendarChange={(dates, dateStrings, info) => {
                setDates([...dates])
                setFrom(dateStrings[0])
                setTo(dateStrings[1])
              }}
              allowClear={false}
            />
          </div>
          <div
            className={`${from && to ? 'cursor-pointer' : 'opacity-60'
              } mt-[15px] w-full h-[24px] flex justify-center items-center bg-[#FFCF55] rounded-[20px] text-[14px] text-[#000000]`}
            onClick={() => onApply()}
          >
            {t('APPLY')}
          </div>
          <div
            className='mt-[11px] w-full h-[24px] flex justify-center items-center text-[14px] text-[#000000] bg-[#E5E5E5] rounded-[20px] cursor-pointer'
            onClick={() => setShowDate(false)}
          >
            {t('CANCEL')}
          </div>
        </motion.div>
      )}
    </div>
  )

  const onApply = () => {
    if (!from || !to) return
    setDate([from, to])
    setShowDate(false)
  }

  //筛选数据
  useEffect(() => {
    if (fromTime && toTime) {
      const results = allData.filter(item => {
        const itemTime = dayjs(item.time).valueOf()

        return fromTime <= itemTime && itemTime <= toTime
      })
      setShowData(formatDateGroup(results))
    } else {
      setShowData(formatDateGroup(allData))
    }
  }, [fromTime, toTime, allData])

  // 工具函数：格式化时间
  function formatDateGroup(data) {
    const grouped = {}

    data.forEach(item => {
      const dateObj = new Date(item.time);
      const dateStr = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      const timeStr = dateObj.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      if (!grouped[dateStr]) {
        grouped[dateStr] = [];
      }
      grouped[dateStr].push({
        time: timeStr,
        title: item.title,
        url: item.url,
      })
    })

    // 转换成数组格式
    const result = Object.entries(grouped).map(([date, list]) => ({
      date,
      list,
    }))

    // const result = Object.entries(grouped).map(([date, list]) => ({
    //   date,
    //   list: [...list].sort((a, b) => {
    //     return dayjs(a.time, 'hh:mm A') - dayjs(b.time, 'hh:mm A') // 每天日期里的数组时间顺序排序
    //   })
    // }))

    // 可选：按日期降序排列
    result.sort((a, b) => new Date(b.date) - new Date(a.date))

    return result
  }

  useEffect(() => {
    const title = t('Browser History')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  return (
    <Fragment>
      <div className='w-full flex smj:flex-col !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-92px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] bg-white relative'>
        {/* 左侧展示 */}
        <div className='w-[330px] flex-shrink-0 smj:w-full border-[#E7E8EC] smjs:border-r-[1px] smj:border-b-[1px] p-[15px]'>
          <div className="relative">
            <input className="h-[40px] w-full text-[14px] placeholder:text-[#818187] text-[#282938] border-[1px] border-[#E3E3E5] rounded-[8px] pl-[44px]"
              placeholder={t('Search Browser History')} />
            <div className='absolute top-0 left-0 flex items-center h-full pl-4'>
              <Image src="/images/contacts/search.svg" className='w-[20px] h-[20px]' width={20} height={20} alt='Search' />
            </div>
          </div>
          <div className='flex items-center justify-between mt-4 p-3 bg-[#F4F5F7] rounded-[6px]'>
            <div className='flex items-center'>
              <Image src={'/images/navigation/chrome.svg'} width={36} height={36} alt='chrome' />
              <div className='text-[16px] text-[#222222] ml-[7px]'>Chrome</div>
            </div>
            <div className='text-[14px] text-[#949494]'>{showData[0]?.date}</div>
          </div>
        </div>
        {/* 右侧展示数据 */}
        <div className='flex-grow relative'>
          <div className='w-full p-[10px] flex items-center justify-between border-[#E7E8EC] smjs:border-b-[1px]'>
            <div className='flex items-center'>
              <Image src={'/images/navigation/chrome.svg'} width={36} height={36} alt='chrome' />
              <div className='text-[16px] font-[600] text-black ml-[7px]'>Chrome</div>
            </div>
            <DateRangeFilter value={date} onChange={setDate} className='w-[calc(50%-5px)] smj:w-[172px] Blds:w-auto Blds:min-w-[172px]' />
          </div>
          <div ref={dom} className='p-[10px] !max-h-[calc(var(--vh2)*100-276px)] smj:!max-h-[calc(var(--vh2)*100-300px)] overflow-y-auto overflow-x-hidden grid gap-4'>
            {showData.map((item, i) => {
              return (
                <div key={i} className='bg-white rounded-[10px] [box-shadow:0px_0px_12px_0px_#0000001A] h-fit relative'>
                  <div className='h-[50px] flex items-center pl-[14px] smj:pl-[10px] border-[#E7E8EC] border-b-[1px]'>
                    <p className='text-[16px] text-[#222222]'>{item.date}</p>
                  </div>
                  <div className='grid gap-4 py-4 pl-[14px] smj:px-[10px] text-[14px] leading-[22px]'>
                    {item.list.map((it, i) => {
                      return (
                        <div key={i} className='grid grid-cols-[auto_1fr] gap-[28px] smj:gap-4'>
                          <p className='text-[#22222299]'>{it.time}</p>
                          <div className='flex smj:flex-col overflow-hidden'>
                            <p className='text-[#222222CC]'>{it.title}</p>
                            <a href={it.url} target="_blank" rel="noopener noreferrer" className='text-[#2A99FF] smjs:ml-[28px] line-clamp-1 break-words'>{it.url}</a>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
          {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(allData.length > 0 || isLoading) ||
            <div className='text-center px-2 absolute inset-0 flex justify-center items-center pointer-events-none'>
              {t('It appears theres no data to show here yet')}<br />
              {t('If data exists it may take up to 1 hour to download and appear')}<br />
              {t('We suggest checking back in a little while')}
            </div>}
        </div>
      </div>
    </Fragment>
  )
}

export default AndroidBrowserHistory
